import { IDocumentTokenDto } from "@/models/document-token.entity";
import { VuetifyMaterialColorEnum } from "@/lib/enum/vuetify-color.enum";
import { BaseTokenEntity } from "./base-token.entity";

function parsePlaceholderToken(token: string): { placeholderKey: string; formatter?: string } {
  const stripped = token.replaceAll("{", "").replaceAll("}", "").trim();
  const formatterIdx = stripped.indexOf("#");
  if (formatterIdx >= 0) {
    const formatter = stripped.slice(0, formatterIdx).trim();
    const placeholderKey = stripped.slice(formatterIdx + 1).trim();
    return {
      formatter: formatter || undefined,
      placeholderKey: placeholderKey || stripped
    };
  }

  return { placeholderKey: stripped };
}

export class PlaceholderTokenEntity extends BaseTokenEntity {
  static readonly SENTINEL = "{{placeholder}}";

  static isPlaceholder(token: string): boolean {
    return token.startsWith("{{") && token !== "{{sign.qr}}" && token !== "{{sign.img}}" && token !== "{{signature}}";
  }

  placeholderKey: string;
  formatter?: string;
  fontSize: number;

  constructor(
    data: Partial<PlaceholderTokenEntity> & {
      x: number;
      y: number;
      w?: number;
      h?: number;
      token?: string;
    }
  ) {
    super({ ...data, value: data.value });
    const parsed = parsePlaceholderToken(data.token ?? PlaceholderTokenEntity.SENTINEL);
    this.placeholderKey = data.placeholderKey ?? parsed.placeholderKey;
    this.formatter = data.formatter ?? parsed.formatter;
    this.fontSize = data.fontSize ?? 12;
  }

  get colorHex() {
    return VuetifyMaterialColorEnum.GREY;
  }

  get icon() {
    return "mdi-variable";
  }

  get color() {
    return "grey";
  }

  get labelKey() {
    return "sign.fieldType.placeholder";
  }

  get tokenString() {
    if (!this.placeholderKey) {
      return PlaceholderTokenEntity.SENTINEL;
    }
    if (this.formatter) {
      return `{{${this.formatter}# ${this.placeholderKey}}}`;
    }
    return `{{${this.placeholderKey}}}`;
  }

  get defaultSize() {
    return { w: 0.3, h: 0.02 };
  }

  get fixedSize() {
    return false;
  }

  validateConstraints(pageWidthMm?: number, pageHeightMm?: number) {
    if (!pageHeightMm) return;
    this.y = Math.max(0, Math.min(pageHeightMm - this.h, this.y));
  }

  toDto(widthPx: number, heightPx: number): IDocumentTokenDto {
    return this.buildDto(widthPx, heightPx, this.fontSize);
  }

  static fromTokenString(
    tokenStr: string,
    data: Partial<PlaceholderTokenEntity> & {
      x: number;
      y: number;
      w?: number;
      h?: number;
    }
  ): PlaceholderTokenEntity {
    return new PlaceholderTokenEntity({
      ...data,
      token: tokenStr
    });
  }
}
